import { forwardRef, useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import TextInput from './TextInput';

/**
 * A password field with a show/hide eye on the right. Pass `visible` + `onToggleVisible` to share one eye between
 * two fields (new + confirm); otherwise it keeps its own. Shown passwords are never autocorrected or capitalised.
 */
const PasswordInput = forwardRef(function PasswordInput(
  { visible: visibleProp, onToggleVisible, showToggle = true, className = '', ...props },
  ref,
) {
  const [ownVisible, setOwnVisible] = useState(false);
  const controlled = typeof visibleProp === 'boolean';
  const visible = controlled ? visibleProp : ownVisible;
  const toggle = () => (controlled ? onToggleVisible?.() : setOwnVisible((v) => !v));

  return (
    <div className="password-input">
      <TextInput
        ref={ref}
        {...props}
        type={visible ? 'text' : 'password'}
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        className={`${showToggle ? 'password-input__field' : ''} ${className}`.trim()}
      />
      {showToggle && (
        <button
          type="button"
          className="password-input__toggle"
          onClick={toggle}
          // Keep the keyboard up and the caret where it was.
          onMouseDown={(e) => e.preventDefault()}
          aria-label={visible ? 'Hide password' : 'Show password'}
          aria-pressed={visible}
          disabled={props.disabled}
          tabIndex={-1}
        >
          {visible ? <EyeOff size={20} aria-hidden="true" /> : <Eye size={20} aria-hidden="true" />}
        </button>
      )}
    </div>
  );
});

export default PasswordInput;
